/* eslint-disable @typescript-eslint/naming-convention */
// internal-deps
import { AnalyticsContextValue, GlobalHeaderState } from "@swiggy-private/checkout-core";
import { SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
import { IIrctcPayload, IrctcAddressChangeFormKeys } from "@swiggy-private/cart-types";

// constants
import { SCREEN_NAME, IMPRESSION_ACTIONS, CLICK_ACTIONS } from "../constants";

export interface IrctcBannerPayload {
    isIrctc?: string;
    flow?: string;
    restaurantId?: string;
    cartId?: string;
    stationName?: string;
    deliveryAt?: string;
    seatNo?: string;
    contactNo?: string;
    statusCode?: number | string;
    pnrNumber?: string;
}

const getSource = (): string => GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) || "";

const buildFlowExtraParams = ({ isIrctc, flow }: IrctcBannerPayload): string =>
    JSON.stringify({ irctc_flag: isIrctc, flow });

export const impressionIrctcUpdateAddress = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    irctcData?: IIrctcPayload,
): void => {
    const { berth, station_name, coach, mobile_number } = irctcData || {};
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.USER_DETAILS_BANNER_IRCTC,
        context: irctcData ? JSON.stringify({ berth, station: station_name, coach, mobile: mobile_number }) : undefined,
        extraParams: JSON.stringify({ is_irctc_event: true, source: getSource() }),
    });
};

export const clickIrctcEditCta = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    ctaType?: IrctcAddressChangeFormKeys,
    irctcData?: IIrctcPayload,
): void => {
    const { berth, coach, mobile_number } = irctcData || {};
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action:
            ctaType === IrctcAddressChangeFormKeys.COACH_NO
                ? CLICK_ACTIONS.USER_DETAILS_SEAT_IRCTC
                : CLICK_ACTIONS.USER_DETAILS_CONTACT_IRCTC,
        context: irctcData ? JSON.stringify({ berth, coach, mobile: mobile_number }) : undefined,
        extraParams: JSON.stringify({ is_irctc_event: true, source: getSource() }),
    });
};

export interface IrctcAddressChangePayload {
    restaurantId?: string;
    orderId?: string;
}

export const impressionIrctcAddressChange = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { restaurantId, orderId }: IrctcAddressChangePayload,
): void => {
    impressionEvent({
        category: SCREEN_NAME.IRCTC_ADDRESS_CHANGE,
        action: IMPRESSION_ACTIONS.UPDATE_DETAILS_IRCTC_ADDRESS_CHANGE,
        label: JSON.stringify({ restaurantId, orderId }),
    });
};

export const clickIrctcAddressChange = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { restaurantId, orderId }: IrctcAddressChangePayload,
): void => {
    clickEvent({
        category: SCREEN_NAME.IRCTC_ADDRESS_CHANGE,
        action: CLICK_ACTIONS.UPDATE_DETAILS_IRCTC_ADDRESS_CHANGE,
        label: JSON.stringify({ restaurantId, orderId }),
    });
};

export const impressionDeliveryBanner = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    payload: IrctcBannerPayload,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_DELIVERY_BANNER,
        label: JSON.stringify({ restaurant_id: payload.restaurantId }),
        context: JSON.stringify({ station_name: payload.stationName, delivery_at: payload.deliveryAt }),
        extraParams: buildFlowExtraParams(payload),
    });
};

export const impressionEnterPnr = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    payload: IrctcBannerPayload,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_ENTER_PNR,
        extraParams: buildFlowExtraParams(payload),
    });
};

export const clickEnterPnr = (clickEvent: AnalyticsContextValue["clickEvent"], payload: IrctcBannerPayload): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_ENTER_PNR,
        extraParams: buildFlowExtraParams(payload),
    });
};

export const impressionFotCartError = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    payload: IrctcBannerPayload,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_FOT_CART_ERROR,
        context: JSON.stringify({ status_code: payload.statusCode }),
        extraParams: buildFlowExtraParams(payload),
    });
};

export const impressionUserDetailsBanner = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    payload: IrctcBannerPayload,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.USER_DETAILS_BANNER_IRCTC,
        label: payload.pnrNumber
            ? JSON.stringify({ restaurant_id: payload.restaurantId, pnr_number: payload.pnrNumber })
            : payload.restaurantId,
        context: JSON.stringify({ seat_no: payload.seatNo, contact_no: payload.contactNo }),
        extraParams: buildFlowExtraParams(payload),
    });
};

export const clickUpdateDetailsBanner = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    payload: IrctcBannerPayload,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.UPDATE_DETAILS_IRCTC,
        label: JSON.stringify({ restaurant_id: payload.restaurantId, cart_id: payload.cartId }),
        context: JSON.stringify({ coach_no: payload.seatNo, contact_no: payload.contactNo }),
        extraParams: buildFlowExtraParams(payload),
    });
};
